"use client";

import { useLayoutEffect, useRef, useState } from "react";
import type { ResolvedMention } from "@/lib/mentions";
import { RichPostText } from "./rich-post-text";

type ExpandablePostTextProps = {
  content: string;
  mentions?: ResolvedMention[];
  authorName?: string | null;
  authorHref?: string | null;
  /** Collapsed clamp height. Expanding removes the clamp permanently. */
  clampLines?: 2 | 3 | 4 | 5 | 6 | 8 | 10;
  className?: string;
  textClassName?: string;
};

/**
 * Truncated post text: clamps to `clampLines` and shows X's "Show more" only
 * when the content actually overflows the clamp.
 */
export function ExpandablePostText({
  content,
  mentions,
  authorName,
  authorHref,
  clampLines = 2,
  className,
  textClassName,
}: ExpandablePostTextProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setExpanded] = useState(false);
  const [isOverflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) return;
    const paragraph = wrapperRef.current?.querySelector("p");
    if (!paragraph) return;

    const measure = () => {
      setOverflowing(paragraph.scrollHeight > paragraph.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(paragraph);
    return () => observer.disconnect();
  }, [isExpanded]);

  return (
    <div ref={wrapperRef} className={className}>
      <RichPostText
        content={content}
        mentions={mentions}
        authorName={authorName}
        authorHref={authorHref}
        lineClamp={isExpanded ? undefined : clampLines}
        className={textClassName}
      />
      {!isExpanded && isOverflowing ? (
        <button
          type="button"
          className="mt-0.5 text-[15px] font-medium text-brand transition-opacity hover:opacity-75"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(true);
          }}
        >
          Show more
        </button>
      ) : null}
    </div>
  );
}
